import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import type { OccasionId } from '@/schemas/occasion.schema'
import type { ProductCategory } from '@/schemas/product.schema'
import { httpClient } from '@/services/http/http-client'
import { ProductsService } from '@/services/products/products.service'

const productsService = new ProductsService(httpClient)

const PRODUCTS_STALE_TIME_MS = 5 * 60 * 1000

export const productsQueryOptions = {
  all: () =>
    queryOptions({
      queryKey: ['products', 'all'] as const,
      queryFn: () => productsService.listAll(),
      staleTime: PRODUCTS_STALE_TIME_MS,
    }),
  byCategory: (category: ProductCategory) =>
    queryOptions({
      queryKey: ['products', 'category', category] as const,
      queryFn: () => productsService.listByCategory(category),
      staleTime: PRODUCTS_STALE_TIME_MS,
    }),
  bySlug: (slug: string) =>
    queryOptions({
      queryKey: ['products', 'slug', slug] as const,
      queryFn: () => productsService.findBySlug(slug),
      staleTime: PRODUCTS_STALE_TIME_MS,
    }),
  byOccasion: (occasion: OccasionId) =>
    queryOptions({
      queryKey: ['products', 'occasion', occasion] as const,
      queryFn: () => productsService.listByOccasion(occasion),
      staleTime: PRODUCTS_STALE_TIME_MS,
    }),
  byOptionalOccasion: (occasion: OccasionId | undefined) =>
    queryOptions({
      queryKey: occasion
        ? (['products', 'occasion', occasion] as const)
        : (['products', 'all'] as const),
      queryFn: () =>
        occasion
          ? productsService.listByOccasion(occasion)
          : productsService.listAll(),
      staleTime: PRODUCTS_STALE_TIME_MS,
    }),
}

export function useProductsAll() {
  return useSuspenseQuery(productsQueryOptions.all())
}

export function useProductsByCategory(category: ProductCategory) {
  return useSuspenseQuery(productsQueryOptions.byCategory(category))
}

export function useProductBySlug(slug: string) {
  return useSuspenseQuery(productsQueryOptions.bySlug(slug))
}

export function useProductsByOccasion(occasion: OccasionId) {
  return useSuspenseQuery(productsQueryOptions.byOccasion(occasion))
}
